import {put, call} from 'redux-saga/effects';
import RedFlagType from "../../entities/redFlagType";
import * as Actions from "../actions/redFlagTypeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RedFlagTypeAction = {
    redFlagTypeId: string;
    redFlagType: RedFlagType;
    redFlagTypes: RedFlagType[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneRedFlagType(action: RedFlagTypeAction) {
	const session = tabSession.captureGeneration();
	try {
		const redFlagType: RedFlagType = yield call(api.findOne, 'redFlagType', action.redFlagTypeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_FETCH_ONE_SUCCESS, redFlagTypeId: action.redFlagTypeId, redFlagType});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_FETCH_ONE_FAILURE, redFlagTypeId: action.redFlagTypeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RedFlagTypeAction} action
 */
export function* saveRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ REDFLAGTYPE_SAVE saga', action.redFlagType?.id);
    
	if (action.redFlagType === undefined || action.redFlagType === null) {
		console.error('saveRedFlagType received a null');
		throw new Error('saveRedFlagType received a null');
	}

	if (!(action.redFlagType instanceof RedFlagType)) {
		action.redFlagType = new RedFlagType(action.redFlagType);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
		if (addResult.isErr()) {
			console.error(`⮂ saveRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.REDFLAGTYPE_SAVE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_SAVE, redFlagType: action.redFlagType});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.REDFLAGTYPE_SAVE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {RedFlagTypeAction} action
 * @param {RedFlagType[]} action.redFlagTypes
 */
export function* saveManyRedFlagTypes(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ REDFLAGTYPE_SAVE_MANY saga', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));
    
    if (!Array.isArray(action.redFlagTypes)) {
        console.error('saveManyRedFlagTypes expected an array, received', action.redFlagTypes);
        throw new Error('saveManyRedFlagTypes expected an array');
    }

	try {
		const toSave: RedFlagType[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.redFlagTypes.length; i++) {
			const redFlagType = action.redFlagTypes[i];
			redFlagType.persisted = false;
			toSave.push(redFlagType);
		}

		const addResult = yield call(idb.add, 'redFlagType', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyRedFlagTypes: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.REDFLAGTYPE_SAVE_MANY_FAILURE, redFlagTypes: action.redFlagTypes, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_SAVE_MANY, redFlagTypes: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.REDFLAGTYPE_SAVE_MANY_FAILURE, redFlagTypes: action.redFlagTypes, status: 500, err});

		console.error(`Error saving ${action.redFlagTypes.length} redFlagTypes to database`, err);
	}
}

/**
 * @param {RedFlagTypeAction} action
 */
export function* removeRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.redFlagType === undefined || action.redFlagType === null || !(action.redFlagType instanceof RedFlagType)) {
			console.error('removeRedFlagType expected type RedFlagType, received', action.redFlagType);
			throw new Error('removeRedFlagType expected type RedFlagType');
		}

	try {
		// Mark as deleted
		action.redFlagType.status = 'D';
		action.redFlagType.addedOn = newHRTimestamp();
		action.redFlagType.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAGTYPE_REMOVE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_REMOVE, redFlagType: action.redFlagType});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_REMOVE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this RedFlagType.
	*
	* @param {RedFlagTypeAction} action
	*/
export function* restoreRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRedFlagType expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.redFlagType === undefined || action.redFlagType === null) {
        console.error('restoreRedFlagType expected a RedFlagType, received null');
        throw new Error('restoreRedFlagType expected a RedFlagType, received null');
    }
    else if (!(action.redFlagType instanceof RedFlagType)) action.redFlagType = new RedFlagType(action.redFlagType); 
    
	try {
		// Mark as restored
		action.redFlagType.status = 'U';
		action.redFlagType.addedOn = newHRTimestamp();
		action.redFlagType.addedBy = userId;
		action.redFlagType.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAGTYPE_RESTORE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_RESTORE, redFlagType: action.redFlagType});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_RESTORE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

		console.error('Error restoring from database', err);
	}
}